/**
 * Browser sign-in against Keycloak (PROJECT_SPEC.md §5.6; SEC-004…008).
 *
 * Authorization code with PKCE, and nothing else. There is no password field in this application and there
 * never will be: the client never sees a credential, so it cannot leak one, log one, or be phished into
 * replaying one. The only secret it holds is a code verifier it generated for a single exchange.
 *
 * Two details that are easy to get wrong and matter here:
 *
 * The `state` is checked on return. Without it, a link could drop an attacker's authorisation code into
 * this application's callback and sign the operator into someone else's session.
 *
 * The verifier lives in session storage, not local storage, and is deleted the moment it is used. It is
 * useful to an attacker for exactly one exchange, so its lifetime is exactly that long.
 */

import { SIGN_IN_ACR_KEY } from "./api.js";
import { isLocked } from "./idle-lock.js";

const ISSUER = (import.meta.env["VITE_OIDC_ISSUER"] as string | undefined) ?? inferIssuer();
const CLIENT_ID = (import.meta.env["VITE_OIDC_CLIENT_ID"] as string | undefined) ?? "ebr-web";
const VERIFIER_KEY = "ebr.pkce.verifier";
const STATE_KEY = "ebr.pkce.state";

/**
 * Where the identity provider lives when nothing says otherwise: alongside this client on the same origin.
 * Same-origin matters — an HTTPS page cannot exchange a code against an HTTP endpoint, so a provider on a
 * different scheme is not merely inconvenient, it cannot work at all.
 */
function inferIssuer(): string {
  const base = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
  const authBase = base.endsWith("/ebr") ? `${base}-auth` : "/ebr-auth";
  return `${window.location.origin}${authBase}/realms/corpex-ebr`;
}

export interface Discovery {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
}

let discovered: Discovery | null = null;

export async function discover(): Promise<Discovery> {
  if (discovered) return discovered;
  const res = await fetch(`${ISSUER}/.well-known/openid-configuration`);
  if (!res.ok) throw new Error(`the identity provider at ${ISSUER} did not answer (${res.status})`);
  discovered = (await res.json()) as Discovery;
  return discovered;
}

function randomString(bytes = 32): string {
  const raw = new Uint8Array(bytes);
  crypto.getRandomValues(raw);
  return base64Url(raw);
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/** Where the provider sends the operator back: this application's own entry point, exactly as registered. */
export function redirectUri(): string {
  const base = import.meta.env.BASE_URL ?? "/";
  return `${window.location.origin}${base}`.replace(/\/$/, "/");
}

/**
 * Begins a sign-in. `acrValues` requests a stronger authentication for a signature step-up; the ordinary
 * sign-in asks for nothing in particular and lets the realm decide.
 */
const RETURN_KEY = "ebr.return";

/** Remembers where a person was when their session had to be renewed, so they come back to it, not to Home. */
export function rememberReturn(loc: Pick<Location, "pathname" | "search" | "hash"> = window.location): void {
  const path = `${loc.pathname}${loc.search}${loc.hash}`;
  if (/[?&](code|state|error)=/.test(loc.search) || path === "/") return;
  sessionStorage.setItem(RETURN_KEY, path);
}

/** The remembered place, once; only a path on this site (never another origin: no open redirect). */
export function takeReturn(): string | null {
  const path = sessionStorage.getItem(RETURN_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  return path && /^\/(?!\/)/.test(path) && !path.includes("\\") ? path : null;
}

/** The level the server requires of every sign-in (SEC-030), or null; best-effort, the 401 path still covers a miss. */
async function publishedSignInAcr(): Promise<string | null> {
  try {
    const res = await fetch(
      `${import.meta.env.BASE_URL ?? "/"}api/v1/sign-in-policy`.replace(/\/{2,}/g, "/"),
      {
        signal: AbortSignal.timeout(5000),
      },
    );
    const acr = res.ok ? ((await res.json()) as { acr?: unknown }).acr : null;
    return typeof acr === "string" && /^[a-z]+$/.test(acr) ? acr : null;
  } catch {
    return null;
  }
}

export async function beginSignIn(acrValues?: string, nonce?: string): Promise<void> {
  // After an inactivity lock (SEC-014) the identity provider's own session must not sign the person straight back in.
  const mustAuthenticate = isLocked(sessionStorage);
  const { authorization_endpoint } = await discover();
  const verifier = randomString();
  const state = randomString(16);
  if (!acrValues) rememberReturn(); // a signature has its own return path (signing intent)
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const url = new URL(authorization_endpoint);
  url.searchParams.set("client_id", CLIENT_ID);
  url.searchParams.set("redirect_uri", redirectUri());
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid profile email");
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", await challengeFor(verifier));
  url.searchParams.set("code_challenge_method", "S256");
  // a signature's nonce binds the re-authentication to that one request (ESIG-034)
  if (nonce) url.searchParams.set("nonce", nonce);
  // an ordinary sign-in asks for the second factor once the server has said it requires one (SEC-030); unlike a
  // signature it may step the existing session up rather than start a new one
  const acr = acrValues ?? (await publishedSignInAcr()) ?? sessionStorage.getItem(SIGN_IN_ACR_KEY);
  if (acr) url.searchParams.set("acr_values", acr);
  if (acrValues || mustAuthenticate) {
    // a step-up (or an unlock) must re-authenticate; an existing session is exactly what it is not allowed to reuse
    url.searchParams.set("prompt", "login");
    url.searchParams.set("max_age", "0");
  }
  window.location.assign(url.toString());
}

export interface SignInResult {
  accessToken: string;
  idToken: string;
  expiresIn: number;
  /** Held in memory only (lib/session.ts); renews the access token before it expires. */
  refreshToken: string | null;
}

/**
 * Completes a sign-in if this page load is a callback. Returns null when it is an ordinary load, so the
 * caller can treat "not a callback" and "signed in" as different things rather than guessing.
 */
// One exchange at a time. React StrictMode runs the sign-in effect twice in development, back to back; the first
// run spends the single-use callback (state, verifier, code), so a second run that started over found no
// callback, redirected to sign in again, and looped. A caller arriving while the exchange is in flight now shares
// it. Once it settles the share ends: after sign-out, asking again must find no callback, never the old tokens.
let inFlight: Promise<SignInResult | null> | null = null;

export function completeSignIn(): Promise<SignInResult | null> {
  if (!inFlight)
    inFlight = exchangeCallback().finally(() => {
      inFlight = null;
    });
  return inFlight;
}

async function exchangeCallback(): Promise<SignInResult | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const returnedState = params.get("state");
  const error = params.get("error");

  if (error)
    throw new Error(`${params.get("error_description") ?? error}. Nothing was signed in; you can try again.`);
  if (!code) return null;

  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  // both are single-use; clearing them first means a replayed callback finds nothing to work with
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  clearCallbackFromUrl();

  if (!expectedState || returnedState !== expectedState)
    throw new Error(
      "This sign-in did not start here, so it was refused. Open the application and sign in again.",
    );
  if (!verifier) throw new Error("The sign-in could not be completed because its verifier is missing.");

  const { token_endpoint } = await discover();
  const res = await fetch(token_endpoint, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: CLIENT_ID,
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }),
  });
  if (!res.ok) {
    const detail = (await res.json().catch(() => ({}))) as { error_description?: string };
    throw new Error(detail.error_description ?? `the identity provider refused the exchange (${res.status})`);
  }
  const token = (await res.json()) as {
    access_token: string;
    id_token: string;
    expires_in: number;
    refresh_token?: string;
  };
  return {
    accessToken: token.access_token,
    idToken: token.id_token,
    expiresIn: token.expires_in,
    refreshToken: token.refresh_token ?? null,
  };
}

/** Exchanges a refresh token for a new set (the provider rotates it); rejects when the provider refuses. */
export async function refreshTokens(
  refreshToken: string,
): Promise<{ accessToken: string; refreshToken: string | null; expiresIn: number }> {
  const { token_endpoint } = await discover();
  const res = await fetch(token_endpoint, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: CLIENT_ID,
      refresh_token: refreshToken,
    }),
  });
  if (!res.ok) throw new Error(`the identity provider refused to renew the session (${res.status})`);
  const t = (await res.json()) as { access_token: string; refresh_token?: string; expires_in: number };
  return { accessToken: t.access_token, refreshToken: t.refresh_token ?? null, expiresIn: t.expires_in };
}

/** Removes the code and state from the address bar so a refresh does not replay a spent callback. */
function clearCallbackFromUrl(): void {
  const url = new URL(window.location.href);
  for (const key of ["code", "state", "session_state", "iss", "error", "error_description"])
    url.searchParams.delete(key);
  window.history.replaceState({}, "", url.toString());
}

export async function signOutUrl(idToken: string): Promise<string | null> {
  const d = await discover();
  if (!d.end_session_endpoint) return null;
  const url = new URL(d.end_session_endpoint);
  url.searchParams.set("id_token_hint", idToken);
  url.searchParams.set("post_logout_redirect_uri", redirectUri());
  return url.toString();
}

/**
 * Ends the identity provider's own session (back-channel logout with the refresh token). Without it "sign out"
 * only forgot the token here, and the next visit was signed straight back in by the provider's cookie: on a
 * shared floor tablet the next person would be working as the previous one (#56). Best effort, never throws.
 */
export async function endProviderSession(refreshToken: string | null): Promise<void> {
  if (!refreshToken) return;
  try {
    const { end_session_endpoint } = await discover();
    if (!end_session_endpoint) return;
    await fetch(end_session_endpoint, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ client_id: CLIENT_ID, refresh_token: refreshToken }),
      signal: AbortSignal.timeout(5000),
    });
  } catch {
    // with no connection the provider's session simply runs out on its own idle clock
  }
}

export const OIDC_ISSUER = ISSUER;
